import { Suspense, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import type { BusinessSummaryItem } from 'src/features/business/shared/schemas/business-item'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'

import { useSearchHistory } from '../api/use-search-history'
import type { SearchHistoryFilter, SearchHistoryItem } from '../api/use-search-history'
import FacelessBusinessSearchResultItem from '../components/faceless-biz-search-result-item'
import TextSearchResultItem from '../components/text-search-result-item'
import SectionHeader from '../components/ui/section-header'
import UserSearchResultItem from '../components/user-search-result-item'
import FacelessBizSearchResultItemSkeleton from '../skeletons/faceless-biz-search-result-item-skeleton'
import SearchHistoryViewSkeleton from '../skeletons/search-history-view-skeleton'
import TextSearchResultItemSkeleton from '../skeletons/text-search-result-item'
import UserSearchResultItemSkeleton from '../skeletons/user-search-result-item-skeleton'

const PAGE_SIZE = 20
const PREVIEW_COUNT = 4

interface SearchHistoryViewContentProps {
  onSelectSearchQuery?: (rawQuery: string) => void
}

const SearchHistoryViewContent = ({ onSelectSearchQuery }: SearchHistoryViewContentProps) => {
  const [filter, setFilter] = useState<SearchHistoryFilter | undefined>(undefined)
  const { ref: loadMoreRef, inView } = useInView({ threshold: 0 })
  const businessHistoryQuery = useSearchHistory({ filter: 'business', limit: PAGE_SIZE })
  const professionalHistoryQuery = useSearchHistory({ filter: 'professional', limit: PAGE_SIZE })
  const searchHistoryQuery = useSearchHistory({ filter: 'search', limit: PAGE_SIZE })

  const searchHistoryItems = searchHistoryQuery.data?.pages.flatMap((page) => page as SearchHistoryItem[])
  const businessHistoryItems = businessHistoryQuery.data?.pages.flatMap((page) => page as BusinessSummaryItem[])
  const professionalHistoryItems = professionalHistoryQuery.data?.pages.flatMap((page) => page as PublicUserData[])

  const activeQuery = useMemo(() => {
    if (filter === 'search') return searchHistoryQuery
    if (filter === 'business') return businessHistoryQuery
    if (filter === 'professional') return professionalHistoryQuery
    return null
  }, [businessHistoryQuery, filter, professionalHistoryQuery, searchHistoryQuery])

  useEffect(() => {
    if (!filter || !activeQuery || !inView) return
    if (!activeQuery.hasNextPage || activeQuery.isFetchingNextPage) return
    activeQuery.fetchNextPage()
  }, [activeQuery, filter, inView])

  const shouldShowSearchHistory = searchHistoryItems?.length > 0 && (!filter || filter === 'search')
  const shouldShowBusinessHistory = businessHistoryItems?.length > 0 && (!filter || filter === 'business')
  const shouldShowProfessionalHistory = professionalHistoryItems?.length > 0 && (!filter || filter === 'professional')

  const visibleSearchItems = filter ? searchHistoryItems : searchHistoryItems?.slice(0, PREVIEW_COUNT)
  const visibleBusinessItems = filter ? businessHistoryItems : businessHistoryItems?.slice(0, PREVIEW_COUNT)
  const visibleProfessionalItems = filter ? professionalHistoryItems : professionalHistoryItems?.slice(0, PREVIEW_COUNT)

  const resetFilter = () => setFilter(undefined)

  return (
    <div className="z-10 flex min-h-[300px] flex-col gap-5">
      {shouldShowSearchHistory && (
        <div className="flex flex-col gap-3">
          <SectionHeader
            title="Recent searches"
            actionLabel={filter === 'search' ? 'Go back' : 'View all'}
            onViewAll={() => (filter === 'search' ? resetFilter() : setFilter('search'))}
            hideAction={searchHistoryItems?.length <= PREVIEW_COUNT}
          />
          <div className="flex flex-col gap-5">
            {visibleSearchItems?.map((item) => (
              <TextSearchResultItem key={item.id} item={item} onClick={onSelectSearchQuery} />
            ))}
            {filter === 'search' && searchHistoryQuery.isFetchingNextPage && (
              <>
                <TextSearchResultItemSkeleton />
                <TextSearchResultItemSkeleton />
              </>
            )}
          </div>
        </div>
      )}
      {shouldShowBusinessHistory && (
        <div className="flex flex-col gap-3">
          <SectionHeader
            title="Recent businesses"
            actionLabel={filter === 'business' ? 'Go back' : 'View all'}
            onViewAll={() => (filter === 'business' ? resetFilter() : setFilter('business'))}
            hideAction={businessHistoryItems?.length <= PREVIEW_COUNT}
          />
          <div className="flex flex-col gap-5">
            {visibleBusinessItems?.map((item) => (
              <FacelessBusinessSearchResultItem key={item.id} business={item} />
            ))}
            {filter === 'business' && businessHistoryQuery.isFetchingNextPage && (
              <>
                <FacelessBizSearchResultItemSkeleton />
                <FacelessBizSearchResultItemSkeleton />
              </>
            )}
          </div>
        </div>
      )}
      {shouldShowProfessionalHistory && (
        <div className="flex flex-col gap-3">
          <SectionHeader
            title="Recent professionals"
            actionLabel={filter === 'professional' ? 'Go back' : 'View all'}
            onViewAll={() => (filter === 'professional' ? resetFilter() : setFilter('professional'))}
            hideAction={professionalHistoryItems?.length <= PREVIEW_COUNT}
          />
          <div className="flex flex-col gap-5">
            {visibleProfessionalItems?.map((item) => (
              <UserSearchResultItem key={item.id} user={item} />
            ))}
            {filter === 'professional' && professionalHistoryQuery.isFetchingNextPage && (
              <>
                <UserSearchResultItemSkeleton />
                <UserSearchResultItemSkeleton />
              </>
            )}
          </div>
        </div>
      )}
      {filter && activeQuery?.hasNextPage && <div ref={loadMoreRef} className="h-2 w-full" />}
    </div>
  )
}

interface SearchHistoryViewProps {
  onSelectSearchQuery?: (rawQuery: string) => void
}

const SearchHistoryView = ({ onSelectSearchQuery }: SearchHistoryViewProps) => {
  return (
    <Suspense fallback={<SearchHistoryViewSkeleton />}>
      <SearchHistoryViewContent onSelectSearchQuery={onSelectSearchQuery} />
    </Suspense>
  )
}

export default SearchHistoryView
